/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import ActionTypes from '@ziynx/action-types';
import { eventNames } from '@ziynx/analytics';
import Colors from '@ziynx/theme/colors';
import { ContentContainer } from '@ziynx/components/layout';
import { withAppManager, useNotification } from '@ziynx/components/utilities';
import { Button } from '@ziynx/components/buttons';
import Link from '@ziynx/components/links';
import Headline from '@ziynx/components/headlines';
import { InputAutocomplete } from '@ziynx/components/inputs';
import { Form } from '@ziynx/components/forms';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import * as KrispyTokens from '@krispy-tokens/blue';

type History = {
  push: (pathName: string, params?: {
    title?: string,
  }) => void,
  action?: string,
};

type Location = {
  state: {
    title?: string,
    backLink?: string,
  },
};

type DispatchAction = {
  type: string,
} => void;

type Props = $ReadOnly<{||}>;

const PageWrapper = styled.div`
  padding: ${({ isMobile }) => (isMobile
    ? `${KrispyTokens.KrispySpacingElementsLdSmall}px ${KrispyTokens.KrispySpacingElementsHdXlarge}px 200px ${KrispyTokens.KrispySpacingElementsHdXlarge}px`
    : `${KrispyTokens.KrispySpacingElementsLdSmall}px ${KrispyTokens.KrispySpacingElementsLdSmall}px 200px ${KrispyTokens.KrispySpacingElementsLdSmall}px`)};
  width: 100%;
  max-width: 688px;
  box-sizing: border-box;
  ::-webkit-scrollbar-track, ::-webkit-scrollbar-thumb, ::-webkit-scrollbar  {
    background-color: transparent;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const LinkWrapper = styled.div`
  padding-bottom: ${KrispyTokens.KrispySpacingElementsHdMedium}px;
`;

const HeadlineWrapper = styled.div`
  padding-bottom: ${KrispyTokens.KrispySpacingElementsLdSmall}px;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: ${({ isNonDesktop }) => (isNonDesktop ? 'column' : 'row')};
  padding-bottom: ${KrispyTokens.KrispySpacingElementsLdSmall}px;
`;

const StyledButton = styled(Button)`
  ${({ lastChild, isMobile, isMobileSmall }) => !lastChild && `margin-${!isMobile && isMobileSmall ? 'bottom' : 'right'}: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`}
`;

const InputWrapper = styled.div`
  padding-bottom: ${({ lastChild }) => (lastChild ? `${KrispyTokens.KrispySpacingElementsLdSmall}px` : `${KrispyTokens.KrispySpacingElementsMdLarge}px`)};
`;

const FilterScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;

  const dispatch: DispatchAction = useDispatch();
  const { notificationDispatch } = useNotification();
  const history: History = useHistory();
  const location: Location = useLocation();

  const filterService = useSelector(({ adminTalentFilters }) => {
    if (Object.keys(adminTalentFilters?.inProgress ?? {}).length) {
      return adminTalentFilters?.inProgress ?? {};
    }
    return adminTalentFilters?.appliedFilters ?? {};
  }, shallowEqual);
  // eslint-disable-next-line max-len
  const scorecardService = useSelector(({ adminTalentScorecards }) => (adminTalentScorecards[ActionTypes.admin.talentFilters.GROUP] || {}), shallowEqual);
  // eslint-disable-next-line max-len
  const scorecardValuesService = useSelector(({ adminTalentScorecardValues = {} }) => adminTalentScorecardValues);
  // eslint-disable-next-line max-len
  const { jobStatus: jobStatusValues, jobType: jobTypeValues, rating: ratingValues } = scorecardValuesService;

  const searchService = useSelector(({ search }) => ({
    collegeOrLeague: search[ActionTypes.search.getCollegeLeague.GROUP] || {},
    ratingType: ratingValues,
    jobStatus: jobStatusValues,
    jobType: jobTypeValues,
  }), shallowEqual);

  /** RESET STATE FOR POST_ADMIN_TALENT_FILTERS_API */
  const clearTalentFilters = () => {
    dispatch({ type: ActionTypes.admin.filters.removeInProgress });
  };

  const formatFilters = (filterVals) => (filterVals || []).map((item) => ({
    _id: item?._id,
    text: item?.name || item?.value,
  }));

  const [
    selectedCollegeLeagues, setCollegeLeagues,
  ] = useState(formatFilters(filterService?.collegeOrLeague) || []);

  const [
    selectedRatingType, setRatingType,
  ] = useState(formatFilters(filterService?.ratingType) || []);
  const [
    selectedJobStatus, setJobStatus,
  ] = useState(formatFilters(filterService?.jobStatus) || []);
  const [
    selectedJobTypes, setJobTypes,
  ] = useState(formatFilters(filterService?.jobTypes) || []);

  const [
    applyClicked, setApplyClicked,
  ] = useState(false);

  const goBack = () => {
    setApplyClicked(false);
    history.push(
      location?.state?.backLink || '/admin/talent',
      { title: location?.state?.title, from: 'Filter' },
    );
  };
  /**
     * CHECK IF USER HITS BROWSEER BACK BUTTON
     * TO CLEAR IN PROGRESS FILTERS IF NOT SAVED
     * @see https://stackoverflow.com/a/61961026
     */
  useEffect(() => () => {
    if (history?.action === 'POP') {
      dispatch({
        type: ActionTypes.admin.filters.removeInProgress,
      });
    }
  }, []);

  useEffect(() => () => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_admin_filter,
    });
  }, []);

  useEffect(() => {
    if (scorecardService?.success
      && !scorecardService?.fetching
      && applyClicked) {
      goBack();
      clearTalentFilters();
    }
    if (!scorecardService?.fetching
      && !scorecardService?.success
      && scorecardService?.error) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Something went wrong, please try again later.',
        },
      });
      setTimeout(() => clearTalentFilters(), 6000);
    }
  }, [scorecardService?.success,
    scorecardService?.fetching,
    scorecardService?.error]);

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';

  const searchActionDispatch = (extraDispatch, field: string) => {
    let type: string = '';
    switch (field) {
    case 'collegeOrLeague':
      type = ActionTypes.search.getCollegeLeague.START;
      break;
    case 'ratingType':
    case 'jobStatus':
    case 'jobType':
      type = ActionTypes.admin.getTalentScorecardValues.START;
      break;
    default:
      break;
    }
    dispatch({
      type,
      ...extraDispatch,
    });
  };

  const onSearch = (field, value) => {
    const query = {
      name: value,
      page: 0,
    };
    searchActionDispatch({ query }, field);
  };

  /** USED FOR SELECTED AND OPTIONS ARRAY */
  const getArrayItems = (valueArray: Array<any>, selected = []) => (
    valueArray || []).filter((i) => !selected.some((j) => j._id === i._id)).map((item) => (
    {
      ...!!item?._id && { _id: item?._id },
      // eslint-disable-next-line no-nested-ternary
      text: item?.name ? item?.name
        : (item?.value ? item?.value : item),
    }));

  const collegeOrLeagueScroll = (e, { hasReachedEnd }) => {
    if (searchService?.collegeOrLeague?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getCollegeLeague.START,
        query: { page: searchService?.collegeOrLeague?.page + 1 },
      });
    }
  };

  const handleFilterSearch = (type) => {
    onSearch(type, '');
  };

  return (
    <>
      <Helmet>
        <title>Filter | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={isMobile ? 86 : 96}>
        <PageWrapper isMobile={isMobile}>
          <LinkWrapper>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              variant="caret"
              onClick={() => {
                // Remove all filters in progress if apply wasn't pressed
                dispatch({
                  type: ActionTypes.admin.filters.removeInProgress,
                });
                goBack();
              }}
              color={Colors.GrayDark}
            >
              Back to&nbsp;
              {location?.state?.title || 'Talent List'}
            </Link>
          </LinkWrapper>

          <HeadlineWrapper>
            <Headline
              variant="page"
              title={{
                text: 'Filter talent list',
                color: Colors.GrayVeryDark,
              }}
            />
          </HeadlineWrapper>
          <Form
            formID="filter-talent-list"
            headingPaddingBottom="0px"
            autocomplete="off"
          >
            <InputWrapper>
              <InputAutocomplete
                label="Rating"
                width="auto"
                showOptionsOnFocus
                {...!(ratingValues ?? []).length && {
                  onFocus: () => onSearch('ratingType', ''),
                }}
                onFocus={() => handleFilterSearch('ratingType')}
                isLoading={scorecardValuesService?.fetching}
                isPaginating={false}
                onChange={(e) => onSearch('ratingType', e?.target?.value)}
                options={
                  getArrayItems(ratingValues, selectedRatingType)
                }
                onClick={(e, option) => {
                  const newRatings = [...selectedRatingType, option];
                  setRatingType(newRatings);
                  dispatch({
                    type: ActionTypes.admin.filters.add,
                    filters: {
                      fieldId: 'rating',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedRatingType}
                onSelectedClick={(e, option) => {
                  const newRatings = selectedRatingType.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setRatingType(newRatings);
                  dispatch({
                    type: ActionTypes.admin.filters.remove,
                    filters: {
                      fieldId: 'rating',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputAutocomplete
                label="Job status"
                width="auto"
                showOptionsOnFocus
                {...!(jobStatusValues ?? []).length && {
                  onFocus: () => onSearch('jobStatus', ''),
                }}
                onFocus={() => handleFilterSearch('jobStatus')}
                isLoading={scorecardValuesService?.fetching}
                isPaginating={false}
                onChange={(e) => onSearch('jobStatus', e?.target?.value)}
                options={
                  getArrayItems(jobStatusValues, selectedJobStatus)
                }
                onClick={(e, option) => {
                  const newJobStatuses = [...selectedJobStatus, option];
                  setJobStatus(newJobStatuses);
                  dispatch({
                    type: ActionTypes.admin.filters.add,
                    filters: {
                      fieldId: 'jobStatus',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedJobStatus}
                onSelectedClick={(e, option) => {
                  const newStatuses = selectedJobStatus.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setJobStatus(newStatuses);
                  dispatch({
                    type: ActionTypes.admin.filters.remove,
                    filters: {
                      fieldId: 'jobStatus',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputAutocomplete
                label="Job types"
                width="auto"
                showOptionsOnFocus
                {...!(jobTypeValues ?? []).length && {
                  onFocus: () => onSearch('jobType', ''),
                }}
                onFocus={() => handleFilterSearch('jobType')}
                isLoading={scorecardValuesService?.fetching}
                isPaginating={false}
                onChange={(e) => onSearch('jobType', e?.target?.value)}
                options={
                  getArrayItems(jobTypeValues, selectedJobTypes)
                }
                onClick={(e, option) => {
                  const newJobTypes = [...selectedJobTypes, option];
                  setJobTypes(newJobTypes);
                  dispatch({
                    type: ActionTypes.admin.filters.add,
                    filters: {
                      fieldId: 'jobType',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedJobTypes}
                onSelectedClick={(e, option) => {
                  const newJobTypes = selectedJobTypes.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setJobTypes(newJobTypes);
                  dispatch({
                    type: ActionTypes.admin.filters.remove,
                    filters: {
                      fieldId: 'jobType',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>
            <InputWrapper lastChild>
              <InputAutocomplete
                label="College or league"
                width="auto"
                showOptionsOnFocus
                {...!(searchService?.collegeOrLeague?.results ?? []).length && {
                  onFocus: () => onSearch('collegeOrLeague', ''),
                }}
                onFocus={() => handleFilterSearch('collegeOrLeague')}
                isLoading={searchService?.collegeOrLeague?.fetching}
                isPaginating={searchService?.collegeOrLeague?.paginating}
                onScroll={collegeOrLeagueScroll}
                onChange={(e) => onSearch('collegeOrLeague', e?.target?.value)}
                options={
                  getArrayItems(searchService?.collegeOrLeague?.results, selectedCollegeLeagues)
                }
                onClick={(e, option) => {
                  const newCollegeLeague = [...selectedCollegeLeagues, option];
                  setCollegeLeagues(newCollegeLeague);
                  dispatch({
                    type: ActionTypes.admin.filters.add,
                    filters: {
                      fieldId: 'collegeOrLeague',
                      content: { _id: option?._id, name: option?.text },
                    },
                  });
                }}
                selected={selectedCollegeLeagues}
                onSelectedClick={(e, option) => {
                  const newCollegeLeague = selectedCollegeLeagues.filter(
                    (item) => item?._id !== option?._id,
                  );
                  setCollegeLeagues(newCollegeLeague);
                  dispatch({
                    type: ActionTypes.admin.filters.remove,
                    filters: {
                      fieldId: 'collegeOrLeague',
                      content: option?.text,
                    },
                  });
                }}
              />
            </InputWrapper>

            <ButtonGroup isNonDesktop={viewport === 'mobileSmall'}>
              <StyledButton
                variant="primary"
                width="100%"
                isMobile={viewport === 'mobile'}
                isMobileSmall={viewport === 'mobileSmall'}
                isLoading={scorecardService?.fetching}
                disabled={scorecardService?.fetching}
                onClick={() => {
                  dispatch({
                    type: ActionTypes.admin.talentFilters.START,
                    body: {
                      filters: {
                        collegeOrLeague:
                          (filterService?.collegeOrLeague || []).map((filter) => filter?._id),
                        ratingType:
                          (filterService?.ratingType || []).map((filter) => filter?._id),
                        jobStatus:
                          (filterService?.jobStatus || []).map((filter) => filter?._id),
                        jobTypes:
                          (filterService?.jobType || []).map((filter) => filter?._id),
                      },
                    },
                  });
                  setApplyClicked(true);
                  dispatch({
                    type: ActionTypes.analytics.logEvent.START,
                    name: eventNames.applied_filters,
                  });
                }}
              >
                Apply
              </StyledButton>
              <Button
                variant="primary"
                width="100%"
                inverted
                disabled={scorecardService?.fetching}
                onClick={() => {
                  if (!Object.keys(filterService).length) {
                    goBack();
                  } else {
                    dispatch({
                      type: ActionTypes.admin.filters.removeAll,
                    });
                    goBack();
                  }
                  dispatch({
                    type: ActionTypes.analytics.logEvent.START,
                    name: eventNames.remove_filters,
                  });
                }}
              >
                Reset
              </Button>

            </ButtonGroup>
          </Form>
        </PageWrapper>

      </ContentContainer>

    </>
  );
};

export default (withAppManager(FilterScreen): React$ComponentType<ManagedProps<Props>>);
